import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOutletsData,
  fetchSigleOutletsData as fetchSigleOutletsDataApi,
} from "../api/marketOverviewApi";
import { restructureFilterOptions } from "../utils/Homepage/homepageUtils";
import { isSupersededResponse } from "../utils/isSupersededResponse";

// Maps the API response into the row shape the outlet table renders
export const transformToOutletData = (data) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  return data.map((item) => {
    const { outlet_info, pet_metric, sku_columns } = item;

    const rawCuisine = outlet_info?.cuisine;
    let processedCuisine = [];

    if (typeof rawCuisine === "string") {
      const trimmedCuisine = rawCuisine.trim();
      if (trimmedCuisine.startsWith("['") && trimmedCuisine.endsWith("']")) {
        // Handles "['item1', 'item2']", "['item']", and "['']"
        const content = trimmedCuisine.slice(2, -2); // Remove "['" and "']"
        if (trimmedCuisine === "['']") {
          // Handles "['']" -> [""]
          processedCuisine = [""];
        } else if (content) {
          // Handles "['item']" or "['item1', 'item2']"
          processedCuisine = content.split("', '").map((c) => c.trim());
        }
        // If content is empty from "[]" after slice (e.g. if input was "[]" but passed the startsWith/endsWith somehow),
        // it would result in processedCuisine = [] which is fine.
        // However, "[]" is handled by the next else-if.
      } else if (trimmedCuisine === "[]") {
        // Handles "[]" -> []
        processedCuisine = [];
      } else if (trimmedCuisine) {
        // Handles a non-empty string not in "['...']" or "[]" format, e.g., "Thai" -> ["Thai"]
        processedCuisine = [trimmedCuisine];
      }
      // If trimmedCuisine is an empty string "", processedCuisine remains []
    } else if (Array.isArray(rawCuisine)) {
      // Handles cases where cuisine is already an array
      processedCuisine = rawCuisine;
    }

    // Fields from outlet_info
    const outletData = {
      key: outlet_info?.id_outlet_info,
      id_outlet_info: outlet_info?.id_outlet_info,
      name: outlet_info?.name,
      location: outlet_info?.address,
      cuisine: processedCuisine,
      platform: outlet_info?.platform,
      franchise: outlet_info?.franchise,
      rating: outlet_info?.rating,
      review: outlet_info?.review,
      price_level: outlet_info?.price_level,
      phone_number: outlet_info?.phone_number,
      // part_of_chain: outlet_info?.chain_flag,
      segment_type: outlet_info?.segment_type,
      geo_level_1: outlet_info?.geo_level_1,
      geo_level_2: outlet_info?.geo_level_2,
      sales_region: outlet_info?.sales_region,
      territory: outlet_info?.territory,
      postal_code: outlet_info?.zip_code,
      address: outlet_info?.address,
      city: outlet_info?.city,
      chain_flag: outlet_info?.part_of_chain,
      url: outlet_info?.url,
      curated_label: outlet_info?.curated_label,
      id_outlet: outlet_info?.id_outlet,
      place_id: outlet_info?.place_id,
      is_place_id_available: outlet_info?.is_place_id_available,
      id_client_crm: outlet_info?.id_client_crm,
      is_crm_available: outlet_info?.is_crm_available,
      custom_fields: outlet_info?.custom_fields || {},

      // Fields from pet_metric
      score: pet_metric?.score,
      score_change: pet_metric?.score_change,
      available: pet_metric?.availability,
      competitor_availability: pet_metric?.competitor_availability,

      core_brand_availability: pet_metric.core_brand_availability,
      // Percentages render as whole numbers
      core_availability:
        typeof pet_metric?.core_availability === "number"
          ? Math.round(pet_metric?.core_availability)
          : parseInt(pet_metric?.core_availability) || 0,

      combo_percentage: pet_metric?.combo_percentage,

      fixed_combo_percentage: pet_metric?.fixed_combo_percentage,

      option_combo_percentage: pet_metric?.option_combo_percentage,

      name_accuracy:
        typeof pet_metric?.name_accuracy === "number"
          ? Math.round(pet_metric?.name_accuracy)
          : parseInt(pet_metric?.name_accuracy) || 0,

      image_accuracy:
        typeof pet_metric?.image_presence === "number"
          ? Math.round(pet_metric?.image_presence)
          : parseInt(pet_metric?.image_presence) || 0,
      pct_correct_image:
        pet_metric?.pct_correct_image !== undefined && pet_metric?.pct_correct_image !== null
          ? typeof pet_metric.pct_correct_image === "number"
            ? Math.round(pet_metric.pct_correct_image)
            : parseInt(pet_metric.pct_correct_image) || null
          : null,

      // Out of Stock fields
      is_out_of_stock:
        pet_metric?.is_out_of_stock !== undefined && pet_metric?.is_out_of_stock !== null
          ? pet_metric.is_out_of_stock
          : null,
      core_brand_out_of_stock: pet_metric?.core_brand_out_of_stock || {},
      // Brand-specific metrics for the brand-metrics layout
      brand_pct_name_accuracy: pet_metric?.brand_pct_name_accuracy || {},
      brand_pct_image_presence: pet_metric?.brand_pct_image_presence || {},

      // The selected SKU's table columns, shaped by the API. Null on the
      // performance tab. withSkuColumns() places them on the SKU tab's row.
      sku_columns: sku_columns ?? null,
    };

    return outletData;
  });
};

const restructureSorterOptions = (sorterOptions) => {
  const defaultSort = {
    score: "desc",
  };

  if (!sorterOptions || !Array.isArray(sorterOptions) || sorterOptions.length === 0) {
    return {
      sort: defaultSort,
    };
  }

  const sorterFieldMappings = {
    score: "score",
    score_change: "score_change",
    review: "review_nr",
    price_level: "price_level",
  };

  // Sort keys the API accepts, built from the user's column sorters
  const sortObject = {};

  sorterOptions.forEach((sorter) => {
    if (sorter.field && sorter.order) {
      const actualField = sorterFieldMappings[sorter.field] || sorter.field;
      const order = sorter.order === "ascend" ? "asc" : "desc";
      sortObject[actualField] = order;
    }
  });

  if (Object.keys(sortObject).length === 0) {
    return {
      sort: defaultSort,
    };
  }

  // // A user sort that omits score falls back to score as a secondary sort
  // if (!sortObject.score) {
  //   return {
  //     sort: {
  //       ...sortObject,
  //       score: "desc", // score as the secondary sort
  //     },
  //   };
  // }

  return {
    sort: sortObject,
  };
};

// Whether this settled list fetch has been overtaken by a newer one, in which
// case its payload is stale and must not land. Export requests never claim the
// sequence, so they are never superseded by it either.
const isSupersededListResponse = (state, action) =>
  !action.meta?.arg?.isExportRequest && isSupersededResponse(state.latestRequestId, action);

export const fetchOutlets = createAsyncThunk(
  "outlet/fetchOutlets",
  async (
    {
      page,
      pageSize,
      firstloading,
      messageApi,
      filterOptions,
      sorterOptions,
      searchTerm,
      country,
      isExportRequest,
      id_client,
      refresh,
      company,
      skuParameters,
      skuId, // ties the returned sku_metric_card to the SKU it belongs to
    },
    { rejectWithValue }
  ) => {
    try {
      if (messageApi)
        messageApi.loading({
          key: "outlet",
          content: "Fetching data...",
          duration: 0,
        });
      // When skuParameters exists, we're in SKU Tab mode
      // Use a non-empty activeTabKey to ensure filters go to sku_filters instead of metric_filters
      // When skuParameters is null/undefined, we're in ONE Performance mode
      const activeTabKey = skuParameters ? "SKU_Tab" : null;
      const restructuredFilters = restructureFilterOptions(
        filterOptions,
        activeTabKey,
        id_client,
        skuParameters
      );
      const restructuredSorter = restructureSorterOptions(sorterOptions);

      const res = await fetchOutletsData({
        page,
        page_size: pageSize,
        filterOptions: { ...restructuredFilters },
        sorterOptions: { ...restructuredSorter },
        searchTerm,
        country,
        id_client,
        refresh,
        company,
        sku_parameters: skuParameters,
      });

      const transformedData = transformToOutletData(res.data);

      if (messageApi)
        messageApi.success({
          key: "outlet",
          content: "Successfully fetching data!",
          durantion: 3,
        });

      return {
        ...res,
        data: transformedData,
        firstloading,
        isExportRequest,
        skuIdRequested: skuId,
      };
    } catch (error) {
      if (messageApi)
        messageApi.error({
          key: "outlet",
          content: "Fail fetching data!",
          durantion: 3,
        });
      return rejectWithValue(error.message || "fail fetching data");
    }
  }
);

export const setFilteredOutlets = createAsyncThunk(
  "outlet/setFilteredOutlets",
  async ({ data }, { rejectWithValue }) => {
    try {
      return { data };
    } catch (error) {
      return rejectWithValue(error.message || "fail set filtered data");
    }
  }
);

// Detail data for one outlet: store availability plus its SKU list
export const fetchSingleOutletData = createAsyncThunk(
  "outlet/fetchSingleOutletData",
  async ({ id_outlet, country, id_client }, { rejectWithValue }) => {
    try {
      if (!id_outlet || !country || !id_client) {
        const missingParams = [];
        if (!id_outlet) missingParams.push("id_outlet");
        if (!country) missingParams.push("country");
        if (!id_client) missingParams.push("id_client");
        throw new Error(`Missing required parameters: ${missingParams.join(", ")}`);
      }

      const data = await fetchSigleOutletsDataApi({
        id_outlet,
        country,
        id_client,
      });

      return {
        storeAvailability: data.store_availbility?.data || [],
        skuList: data.sku_list || [],
        skuInfo: data.sku_info?.data || [],
      };
    } catch (error) {
      console.error("Error fetching single outlet data:", error);
      return rejectWithValue(error.message || "fail fetching single outlet data");
    }
  }
);

const initialState = {
  outlets: [],
  originalOutlets: [],
  loading: false,
  error: null,
  // requestId of the most recent list fetch. Responses from any earlier fetch
  // are dropped, so overlapping requests resolve last-requested-wins rather
  // than last-returned-wins. Export requests are excluded — they run alongside
  // the list and never replace it.
  latestRequestId: null,
  currentOutlet: null,
  firstLoading: true,
  petPlatformData: {}, // Add pet_platform data storage
  petTotalData: null, // Add pet total data storage for "Total DSFs"
  pageInfo: {
    page: 1,
    pageSize: 20,
    total: null,
    totalPages: null,
    pet_score: null,
    pct_pet_availability: null,
    pct_pet_bundles: null,
    pct_pet_image_presence: null,
    pct_pet_name_accuracy: null,
    country: null,
    refresh: null,
    pet_score_change: null,
    core_brands: [],
    image_audit_feature_enabled: true, // Default to true for backward compatibility
    out_of_stock_feature_enabled: false, // Default to false, enabled per client+country
  },
  // Aggregate metrics per selected SKU: { [skuId]: { availability, bundles, content, total_dsf, ... } }
  skuMetricCards: {},
  // Store availability and SKU list state
  storeAvailability: [],
  skuList: [],
  skuInfo: [],
  storeAvailabilityLoading: false,
  skuListLoading: false,
  storeAvailabilityError: null,
  skuListError: null,
};

const outletSlice = createSlice({
  name: "outlet",
  initialState,
  reducers: {
    setCurrentOutlet: (state, action) => {
      state.currentOutlet = action.payload;
    },
    clearCurrentOutlet: (state) => {
      state.currentOutlet = null;
    },
    // Clear all outlets data (for workspace switch)
    clearAllOutletsData: (state) => {
      state.outlets = [];
      state.originalOutlets = [];
      state.currentOutlet = null;
      state.firstLoading = true;
      state.petPlatformData = {};
      state.petTotalData = null;
      state.pageInfo = {
        page: 1,
        pageSize: 20,
        total: null,
        totalPages: null,
        pet_score: null,
        pct_pet_availability: null,
        pct_pet_bundles: null,
        pct_pet_image_presence: null,
        pct_pet_name_accuracy: null,
        country: null,
        refresh: null,
        pet_score_change: null,
        core_brands: [],
        image_audit_feature_enabled: true,
      };
      state.skuMetricCards = {};
      state.storeAvailability = [];
      state.skuList = [];
      state.skuInfo = [];
    },
    // Store availability and SKU list actions
    clearStoreAvailabilityData: (state) => {
      state.storeAvailability = [];
      state.skuList = [];
      state.storeAvailabilityError = null;
      state.skuListError = null;
    },
    setStoreAvailabilityData: (state, action) => {
      state.storeAvailability = action.payload.storeAvailability || [];
      state.skuList = action.payload.skuList || [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutlets.pending, (state, action) => {
        if (!action.meta.arg.isExportRequest) {
          state.loading = true;
          // Claim the sequence: whatever was already in flight is superseded.
          state.latestRequestId = action.meta.requestId;
        }
        state.error = null;
      })
      .addCase(fetchOutlets.fulfilled, (state, action) => {
        // Drop a superseded response. Two list fetches overlap whenever
        // something applies filters while the page's own fetch is in flight —
        // the persona-config arrival preset, a command-palette deep link, a saved
        // filter view. Without this the slower response wins and the table
        // renders rows the applied filters exclude, under chips that say they
        // are applied.
        //
        // Its DATA is stale, but the request did complete, so the one-shot
        // first-load gate still has to settle: only the page's own initial
        // fetch carries `firstloading`, and that is exactly the request a
        // preset supersedes. Leaving it set holds the skeleton up forever.
        // `loading` deliberately stays as it is — a newer fetch is in flight.
        if (isSupersededListResponse(state, action)) {
          if (action.payload?.firstloading) state.firstLoading = false;
          return;
        }
        const {
          data,
          page,
          firstloading,
          limit,
          aggregate_metrics,
          isExportRequest,
          refresh,
          totalPages,
          country,
          image_audit_feature_enabled,
          out_of_stock_feature_enabled,
        } = action.payload;

        const currentSkuId = action.meta?.arg?.skuId; // used for skuMetricCards mapping

        const newCoreBrands = aggregate_metrics?.core_brands
          ? Object.keys(aggregate_metrics?.core_brands)
          : [];
        // Preserve existing core_brands when filter returns no data
        const coreBrands =
          newCoreBrands.length > 0 ? newCoreBrands : state.pageInfo.core_brands || [];

        // Store pet_platform data (clear when no data)
        state.petPlatformData = aggregate_metrics?.pet_platform || {};

        // Store pet total data for "Total DSFs" (clear when no data)
        state.petTotalData = aggregate_metrics?.pet || null;

        if (isExportRequest) {
          state.loading = false;
          return;
        }
        state.loading = false;
        state.outlets = data;
        state.error = null;
        // Robust pagination derivation to handle APIs that don't echo 'limit'
        const requestedPageSize = action.meta?.arg?.pageSize;
        const effectivePageSize =
          typeof limit !== "undefined" && limit !== null
            ? limit
            : typeof requestedPageSize !== "undefined"
              ? requestedPageSize
              : (state.pageInfo.pageSize ?? 20);

        // Pagination counts the page query's rows. The brand-metrics layout
        // lists one row per (outlet, CRM id), so this can exceed the outlet
        // count the score cards show.
        const totalRows = action.payload.total;
        const effectivePage =
          typeof page !== "undefined" && page !== null
            ? page
            : (action.meta?.arg?.page ?? state.pageInfo.page ?? 1);

        const effectiveTotalPages =
          typeof totalPages !== "undefined" && totalPages !== null
            ? totalPages
            : totalRows && effectivePageSize
              ? Math.ceil(totalRows / effectivePageSize)
              : state.pageInfo.totalPages;

        // state.originalOutlets = data;
        state.pageInfo = {
          page: effectivePage,
          pageSize: effectivePageSize,
          total: totalRows,
          totalPages: effectiveTotalPages,
          pet_score: aggregate_metrics?.outlet_metric_card?.agg_pet_score,
          pct_pet_availability: aggregate_metrics?.outlet_metric_card?.availability || 0,
          pct_pet_bundles: aggregate_metrics?.outlet_metric_card?.bundles || 0,
          pct_pet_image_presence: aggregate_metrics?.outlet_metric_card?.image_availability || 0,
          pct_pet_name_accuracy: aggregate_metrics?.outlet_metric_card?.name_accuracy || 0,
          country: country,
          refresh: refresh,
          pet_score_change: aggregate_metrics?.outlet_metric_card?.agg_pet_score_change,
          core_brands: coreBrands,
          image_audit_feature_enabled:
            typeof image_audit_feature_enabled !== "undefined" ? image_audit_feature_enabled : true, // Default to true if not provided
          out_of_stock_feature_enabled:
            typeof out_of_stock_feature_enabled !== "undefined"
              ? out_of_stock_feature_enabled
              : false, // Default to false if not provided
        };
        // A single-SKU aggregate is only storable when the request named its SKU
        const skuMetricCard = aggregate_metrics?.sku_metric_card;
        if (currentSkuId && skuMetricCard) {
          state.skuMetricCards[currentSkuId] = {
            ...skuMetricCard,
          };
        }
        if (firstloading) {
          state.firstLoading = false;
        }
      })
      .addCase(fetchOutlets.rejected, (state, action) => {
        // Same sequence check: a stale failure must not clear the spinner the
        // request still in flight put up, nor report its error as the current
        // one. The first-load gate settles either way, for the same reason as
        // in the fulfilled case above.
        if (isSupersededListResponse(state, action)) {
          if (action.meta?.arg?.firstloading) state.firstLoading = false;
          return;
        }
        state.loading = false;
        state.error = action.error.message;
        // Settle the first-load gate on failure too, so a fresh page load whose
        // very first fetch fails renders the error state instead of an endless skeleton.
        if (action.meta?.arg?.firstloading) state.firstLoading = false;
      })
      .addCase(setFilteredOutlets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setFilteredOutlets.fulfilled, (state, action) => {
        state.loading = false;
        const { data } = action.payload;
        state.outlets = data;
      })
      .addCase(setFilteredOutlets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Single-outlet detail fetch
      .addCase(fetchSingleOutletData.pending, (state) => {
        state.storeAvailabilityLoading = true;
        state.skuListLoading = true;
        state.storeAvailabilityError = null;
        state.skuListError = null;
      })
      .addCase(fetchSingleOutletData.fulfilled, (state, action) => {
        state.storeAvailabilityLoading = false;
        state.skuListLoading = false;
        state.storeAvailability = action.payload.storeAvailability;
        state.skuList = action.payload.skuList;
        state.skuInfo = action.payload.skuInfo;
        state.storeAvailabilityError = null;
        state.skuListError = null;
      })
      .addCase(fetchSingleOutletData.rejected, (state, action) => {
        state.storeAvailabilityLoading = false;
        state.skuListLoading = false;
        state.storeAvailabilityError = action.payload;
        state.skuListError = action.payload;
      });
  },
});

export const {
  setCurrentOutlet,
  clearCurrentOutlet,
  clearAllOutletsData,
  clearStoreAvailabilityData,
  setStoreAvailabilityData,
} = outletSlice.actions;

export default outletSlice.reducer;
